import { useEffect, useMemo, useState } from "react";
import { colors } from "../../Config/Colors";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  Customized,
} from "recharts";
import CustomStyles from "../../CustomStyles";

export function monthName(monthIndex) {
  const months = {
    1: "Ocak",
    2: "Şubat",
    3: "Mart",
    4: "Nisan",
    5: "Mayıs",
    6: "Haziran",
    7: "Temmuz",
    8: "Ağustos",
    9: "Eylül",
    10: "Ekim",
    11: "Kasım",
    12: "Aralık",
  };
  return months[monthIndex] || "";
}

const CHART_COLORS = {
  ...CustomStyles.productColors,
  "Espresso Bazlı": CustomStyles.productColors.Espresso,
};

// Kısaltılmış sayı formatı (3 basamak)
// Sütun animasyonu (#233): seriler dalga halinde büyür, ease-out ile yumuşak
// iner. Gölge filtresi KALDIRILDI — CSS drop-shadow, boyu değişen her sütunda
// her karede yeniden boyama yaptırıyor, animasyonu kasıyordu; yerine dikey
// gradyan (boyama maliyeti yok) sütunları daha belirgin yapıyor.
const BAR_ANIMATION_MS = 900;
const BAR_STAGGER_MS = 90;
const gradientId = (dataKey) => `bar-grad-${String(dataKey).replace(/[^a-zA-Z0-9]/g, "")}`;

const formatCompactValue = (value) => {
  if (value == null || value === 0) return "";
  if (value >= 1_000_000) {
    const v = value / 1_000_000;
    return v >= 100 ? `${Math.round(v)}M` : v >= 10 ? `${Math.round(v * 10) / 10}M` : `${Math.round(v * 100) / 100}M`;
  }
  if (value >= 1_000) {
    const v = value / 1_000;
    return v >= 100 ? `${Math.round(v)}B` : v >= 10 ? `${Math.round(v * 10) / 10}B` : `${Math.round(v * 100) / 100}B`;
  }
  return Math.round(value).toString();
};

// Y ekseni için: 0 gizlenmemeli, geri kalanı kısaltılmış format
const formatAxisValue = (value) =>
  value === 0 ? "0" : formatCompactValue(value);

const labelFontSize = (screenWidth) =>
  screenWidth >= 1280 ? 13 : screenWidth >= 1024 ? 12 : screenWidth >= 768 ? 11 : 9;

/**
 * Sütun değerlerini TEK bir katmanda, tüm çubukların ÜSTÜNE çizer.
 *
 * NEDEN AYRI KATMAN: `<Bar label={...} />` etiketi o serinin kendi <g>'si
 * içinde üretir. SVG'de sonra çizilen üste geldiği için bir sonraki serinin
 * çubuğu, önceki serinin etiketini kesiyordu (grafikte "3.93B" komşu yeşil
 * çubuğun altında kalıyordu). `Customized` en son child olarak eklendiğinden
 * bu katman her zaman çubukların üstünde kalır.
 *
 * `formattedGraphicalItems` Recharts'ın hesapladığı çubuk geometrisidir;
 * her eleman serinin `data` dizisini (x, y, width, height + payload) taşır.
 */
const BarValueLabels = ({ formattedGraphicalItems = [], screenWidth, seriesCount = 1 }) => {
  const fontSize = labelFontSize(screenWidth);
  // Etiketler sütunlar büyürken son konumlarında beliriyordu; artık sütun
  // animasyonu bitince yumuşakça gelir. Çağıran `key` olarak veri imzasını
  // verir: katman yalnız veri değişince yeniden mount olur → solma tekrar oynar.
  const delay = BAR_ANIMATION_MS + BAR_STAGGER_MS * Math.max(0, seriesCount - 1);
  return (
    <g className="motion-safe:animate-labelFade" style={{ animationDelay: `${delay}ms` }}>
      {formattedGraphicalItems.map((item, seriesIndex) => {
        const itemProps = item?.props || {};
        if (itemProps.hide) return null;
        const data = itemProps.data || [];
        const seriesKey = itemProps.dataKey ?? item?.item?.props?.dataKey;
        return data.map((bar, barIndex) => {
          // Ham değer önce payload'dan; Recharts `value` alanı stacked'de
          // [alt, üst] dizisi olabildiği için ikinci kaynak olarak durur.
          // DİKKAT: sonucu number'a daraltmadan formatlama — `false` gibi bir
          // değer Math.round'dan geçip her etikete "0" yazdırıyordu.
          const payloadValue =
            seriesKey != null ? bar?.payload?.[seriesKey] : undefined;
          const rawValue =
            typeof payloadValue === "number"
              ? payloadValue
              : Array.isArray(bar?.value)
                ? bar.value[1]
                : bar?.value;
          const text =
            typeof rawValue === "number" ? formatCompactValue(rawValue) : "";
          if (!text) return null;
          // Sayı, sütunun üstünden sağ-üste doğru çapraz (-45°) yerleşir
          const cx = bar.x + bar.width / 2;
          const cy = bar.y - 6;
          return (
            <text
              key={`${seriesIndex}-${barIndex}`}
              x={cx}
              y={cy}
              fill="#4b5563"
              textAnchor="start"
              fontSize={fontSize}
              fontWeight={700}
              fontFamily="Inter, sans-serif"
              transform={`rotate(-45, ${cx}, ${cy})`}
            >
              {text}
            </text>
          );
        });
      })}
    </g>
  );
};

// Özel Tooltip bileşeni
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 border border-gray-200 rounded-xl shadow-xl backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <p className="font-bold text-gray-800 text-sm">{label}</p>
        </div>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full shadow-sm"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm font-medium text-gray-600">
                  {entry.name}
                </span>
              </div>
              <span className="text-sm font-bold text-gray-800">
                {entry.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

/** Genişliği kırılım noktasına indirger (480 / 640 / 768 / 1024 / 1280) */
const BREAKPOINTS = [1280, 1024, 768, 640, 480];
export const widthBucket = (width) => BREAKPOINTS.find((bp) => width >= bp) ?? 0;

/**
 * Aylık tüketimden grafik modeli — saf. Yalnız en az bir ayda tüketimi olan
 * ürünler seri olur; seriler son ayın tüketimine göre sıralanır.
 */
export function buildChartModel(monthlyConsumption) {
  if (!monthlyConsumption.length) return { chartData: [], seriesList: [] };
  const productNames = Object.keys(CHART_COLORS);
  const activeProducts = productNames.filter((productName) =>
    monthlyConsumption.some((log) =>
      log.productConsument.some(
        (product) => product.productName === productName && product.consument > 0
      )
    )
  );

  const chartData = monthlyConsumption.map((log) => {
    const monthData = { month: monthName(log.monthIndex) };
    activeProducts.forEach((productName) => {
      monthData[productName] = 0;
    });
    log.productConsument.forEach((product) => {
      if (activeProducts.includes(product.productName)) {
        monthData[product.productName] = product.consument || 0;
      }
    });
    return monthData;
  });

  const lastMonth = monthlyConsumption[monthlyConsumption.length - 1];
  const productTotals = {};
  activeProducts.forEach((productName) => {
    productTotals[productName] = 0;
  });
  lastMonth.productConsument.forEach((product) => {
    if (activeProducts.includes(product.productName)) {
      productTotals[product.productName] = product.consument || 0;
    }
  });
  const seriesList = Object.entries(productTotals)
    .sort(([, a], [, b]) => b - a)
    .map(([productName]) => ({
      dataKey: productName,
      name: productName,
      color: CHART_COLORS[productName],
    }));

  return { chartData, seriesList };
}

export default function ConsumptionGraph({
  monthlyConsumption = [],
  dashboardType,
  dashboardConsumentIsLoading,
  warn,
  dashboardConsumentIsFetching,
}) {
  const [visibleMonths, setVisibleMonths] = useState(6);

  // Veri imzası — socket aynı tüketimi yeni nesnelerle tekrar gönderdiğinde
  // grafik verisi AYNI referansta kalır; Recharts sütun animasyonunu yalnız
  // veri gerçekten değişince oynatır (eskiden her yayında baştan büyüyor,
  // useEffect+setState de ikinci bir render yapıyordu).
  const dataSignature = useMemo(
    () =>
      JSON.stringify(
        monthlyConsumption.map((log) => [
          log.monthIndex,
          (log.productConsument || []).map((p) => [p.productName, p.consument]),
        ])
      ),
    [monthlyConsumption]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { chartData, seriesList } = useMemo(() => buildChartModel(monthlyConsumption), [dataSignature]);

  // Yalnız kırılım noktası değişince render — her piksel resize'da değil.
  // Eşikler aşağıdaki tüm `screenWidth >= …` karşılaştırmalarıyla aynı.
  const [screenWidth, setScreenWidth] = useState(() => widthBucket(window.innerWidth));

  // Ekran genişliğini izle
  useEffect(() => {
    let frame = null;
    const apply = () => {
      frame = null;
      const width = window.innerWidth;
      setScreenWidth(widthBucket(width));
      if (width < 480) {
        setVisibleMonths(2);
      } else if (width < 640) {
        setVisibleMonths(3);
      } else if (width < 768) {
        setVisibleMonths(4);
      } else {
        setVisibleMonths(6);
      }
    };

    // rAF ile tek kareye toplanır; setState aynı değerde render yapmaz
    const handleResize = () => {
      if (frame == null) frame = window.requestAnimationFrame(apply);
    };
    window.addEventListener("resize", handleResize);
    apply();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Son n ayı al
  const visibleData = useMemo(() => chartData.slice(-visibleMonths), [chartData, visibleMonths]);

  if (seriesList.length === 0) {
    return (
      <div className="flex w-full h-full items-center justify-center font-medium tracking-wide">
        <p className={`text-lg ${colors.text.primary} p-4 rounded-md`}>
          {warn}
        </p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex w-full h-full items-center justify-center font-medium tracking-wide">
        <p className={`text-lg ${colors.text.primary} p-4 rounded-md`}>
          {warn}
        </p>
      </div>
    );
  }

  const isRefreshing = dashboardConsumentIsFetching && chartData.length > 0;

  return (
    <div className={`w-full h-full flex flex-col transition-opacity duration-300 ${isRefreshing ? "opacity-60" : "opacity-100"}`}>
      {/* Legend - Top */}
      {seriesList.length > 0 && (
        <div className="flex flex-wrap gap-3 sm:gap-4 lg:gap-5 mb-3 sm:mb-4 justify-center px-4">
          {seriesList.slice(0, 6).map((series, index) => (
            <div key={series.dataKey} className="flex items-center gap-1.5 sm:gap-2">
              <div
                className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-3.5 lg:h-3.5 rounded-full shadow-sm"
                style={{ backgroundColor: series.color }}
              />
              <span className="text-xs sm:text-sm lg:text-base font-semibold text-gray-600">
                {series.name}
              </span>
            </div>
          ))}
          {seriesList.length > 6 && (
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-3.5 lg:h-3.5 rounded-full bg-gray-300" />
              <span className="text-xs sm:text-sm lg:text-base font-medium text-gray-500">
                +{seriesList.length - 6} daha
              </span>
            </div>
          )}
        </div>
      )}

      {/* Chart Container */}
      <div className="flex-1 relative">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={visibleData}
            barCategoryGap="40%"
            barGap={10}
            margin={{
              top: 34,
              right: 10,
              left: 0,
              bottom: 0,
            }}
          >
            {/* Seri başına dikey gradyan — filtre yok, boyama maliyeti yok */}
            <defs>
              {seriesList.map((series) => (
                <linearGradient key={series.dataKey} id={gradientId(series.dataKey)} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={series.color} stopOpacity={1} />
                  <stop offset="100%" stopColor={series.color} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>
            {/* Sadece yatay çizgiler — dikeyler sütunlarla yarışıyor */}
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              strokeOpacity={0.6}
            />
            <XAxis
              dataKey="month"
              tick={{
                fontSize: screenWidth >= 1280 ? 15 : screenWidth >= 1024 ? 14 : screenWidth >= 768 ? 13 : 11,
                fontWeight: 700,
                fill: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb", strokeWidth: 1 }}
              tickMargin={10}
              height={40}
            />
            <YAxis
              tick={{
                fontSize: screenWidth >= 1280 ? 14 : screenWidth >= 1024 ? 13 : screenWidth >= 768 ? 12 : 10,
                fontWeight: 600,
                fill: "#6b7280",
                fontFamily: "Inter, sans-serif",
              }}
              tickLine={false}
              axisLine={false}
              // Ham "100000" sol kenardan taşıyordu; kısaltılmış format +
              // sabit genişlik ile eksen artık kırpılmıyor.
              tickFormatter={formatAxisValue}
              width={screenWidth >= 768 ? 48 : 36}
              domain={[0, (dataMax) => {
                if (!dataMax || dataMax === 0) return 100;
                const magnitude = Math.pow(10, Math.floor(Math.log10(dataMax)));
                const step = magnitude >= 10000 ? magnitude / 2 : magnitude;
                return Math.ceil(dataMax * 1.12 / step) * step;
              }]}
              allowDataOverflow={false}
            />
            {/* Zebra arka plan bantları */}
            {visibleData.map((entry, i) =>
              i % 2 === 1 ? (
                <ReferenceArea
                  key={`bg-${i}`}
                  x1={entry.month}
                  x2={entry.month}
                  fill="#d1d5db"
                  fillOpacity={0.22}
                  ifOverflow="extendDomain"
                />
              ) : null
            )}
            <Tooltip content={<CustomTooltip />} />
            {seriesList.map((series, index) => (
              <Bar
                key={series.dataKey}
                dataKey={series.dataKey}
                name={series.name}
                fill={`url(#${gradientId(series.dataKey)})`}
                radius={[5, 5, 0, 0]}
                barSize={screenWidth >= 1280 ? 15 : screenWidth >= 1024 ? 14 : screenWidth >= 768 ? 11 : 8}
                isAnimationActive
                animationBegin={index * BAR_STAGGER_MS}
                animationDuration={BAR_ANIMATION_MS}
                animationEasing="ease-out"
              />
            ))}
            {/* Etiket katmanı EN SONDA — çubukların üstünde kalması için */}
            {/* Element olarak verilir: satır içi fonksiyon her render'da yeni
                bileşen olup etiket katmanını yeniden mount ediyordu */}
            <Customized
              component={
                <BarValueLabels
                  key={dataSignature}
                  screenWidth={screenWidth}
                  seriesCount={seriesList.length}
                />
              }
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
